import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import LoanOfficerProfile from '../../pages/Agents/LoanOfficerProfile';
import IntakeFormDynamic from '../../pages/BuilderIntake/IntakeFormDynamic';

// Loan program pages — surfaced at top-level slugs (e.g. jethl.com/conventional)
import ConventionalLoanPage from '../../pages/LoanPrograms/ConventionalLoan';
import VALoanPage from '../../pages/LoanPrograms/VALoan';
import FHALoanPage from '../../pages/LoanPrograms/FHA';
import USDALoanPage from '../../pages/LoanPrograms/USDALoan';
import RefinancingPage from '../../pages/LoanPrograms/Refinancing';
import JumboLoanPage from '../../pages/LoanPrograms/JumboLoan';
import FirstTimeHomebuyersPage from '../../pages/LoanPrograms/FirstTimeHomebuyers';
import LowDownPaymentPage from '../../pages/LoanPrograms/LowDownPayment';
import DSCRLoanPage from '../../pages/LoanPrograms/DSCRLoan';
import ARMLoanPage from '../../pages/LoanPrograms/ARMLoan';

import { apigetPublic } from '../../service/api';
import LandingPageNav from '../landingpage/LandingPageNav';
import { ProfileHeroSkeleton } from '../skeletons/PageSkeletons';

// Static map of loan program slugs → page components.
// Resolved synchronously before any API calls so these pages render
// instantly with no loading flash.
const LOAN_PROGRAM_PAGES = {
  conventional: ConventionalLoanPage,
  va: VALoanPage,
  fha: FHALoanPage,
  usda: USDALoanPage,
  refinancing: RefinancingPage,
  jumbo: JumboLoanPage,
  'first-time-homebuyers': FirstTimeHomebuyersPage,
  'low-down-payment': LowDownPaymentPage,
  dscr: DSCRLoanPage,
  arm: ARMLoanPage,
};

export default function SlugRouter() {
  const { slug } = useParams();
  const safeSlug = (slug ?? '').toLowerCase();

  // 'loading' | 'loanOfficer' | 'form' | 'notfound'
  const [state, setState] = useState('loading');
  const [intakeCfg, setIntakeCfg] = useState(null);
  const [officer, setOfficer] = useState(null);

  // Sync resolution: if the slug is a known loan program, render it immediately.
  const StaticLoanProgramPage = LOAN_PROGRAM_PAGES[safeSlug];

  useEffect(() => {
    // Skip API calls when we already matched a static loan program slug.
    if (StaticLoanProgramPage) return undefined;

    let cancelled = false;

    (async () => {
      try {
        /* ----------------------------------------
           1️⃣ Check Loan Officer FIRST
        ---------------------------------------- */
        let loanOfficerData = null;
        try {
          const loRes = await apigetPublic(
            `loanofficer/${encodeURIComponent(safeSlug)}`
          );
          if (loRes?.status === 200) loanOfficerData = loRes.data;
        } catch {
          loanOfficerData = null;
        }

        if (cancelled) return;

        if (loanOfficerData) {
          // Hand the record down so the profile page renders without a second fetch.
          setOfficer(loanOfficerData);
          setState('loanOfficer');
          return;
        }

        /* ----------------------------------------
           2️⃣ Check Builder Intake by slug (backend)
        ---------------------------------------- */
        try {
          const intakeRes = await apigetPublic(
            `intake/slug/${encodeURIComponent(safeSlug)}`
          );

          if (intakeRes?.status === 200) {
            setIntakeCfg(intakeRes.data);
            setState('form');
            return;
          }
        } catch {
          // ignore
        }

        if (!cancelled) setState('notfound');
      } catch {
        if (!cancelled) setState('notfound');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [safeSlug, StaticLoanProgramPage]);

  /* ----------------------------------------
     Render
  ---------------------------------------- */

  // Loan program pages take precedence and render synchronously.
  if (StaticLoanProgramPage) {
    return <StaticLoanProgramPage />;
  }

  if (state === 'loading') {
    // Most public slugs are loan officer pages, so show that page's frame
    // while we resolve the slug.
    return (
      <>
        <LandingPageNav hideApply />
        <div style={{ minHeight: '70vh', background: 'rgba(245,245,245,.85)' }}>
          <ProfileHeroSkeleton />
        </div>
      </>
    );
  }

  if (state === 'loanOfficer') {
    return <LoanOfficerProfile initialOfficer={officer} />;
  }

  if (state === 'form') {
    return (
      <IntakeFormDynamic
        config={intakeCfg}
        slug={safeSlug}
      />
    );
  }

  return <div style={{ padding: 24 }}>Not found</div>;
}
